/**
 * InsightsListView Component
 *
 * Displays a list of InsightsAnalysis documents in a table with real-time updates.
 * Pattern: follows StrategiesListView.
 *
 * Features:
 * - Table with sortable columns (name, status, actors count, created)
 * - Action menu per row (view, export, delete)
 * - Empty state with "Analyze Actors" CTA
 * - Real-time Firestore subscription
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Stack,
  MenuItem,
  Button,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  FileDownload as FileDownloadIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';
import {
  SDataTable,
  type SDataTableColumnDef,
  type SDataTableRowSelectionState,
} from '@modules/ui-components/dialogs/SDataTable';
import { InsightsFirestoreService, type InsightsAnalysisDoc } from '../services/InsightsFirestoreService';
import { InsightsExportService } from '../services/InsightsExportService';
import { CreateInsightsDialog } from './CreateInsightsDialog';
import { TrustChip } from './TrustChip';
import { trustChipTestId } from '../testIds';
import {
  averageAudienceScore,
  formatReadiness,
  getB2BAudienceMetrics,
} from '../utils/b2bAudienceMetrics';
import { toDate } from '../utils/toDate';

// ── Types ────────────────────────────────────────────────────────────

export interface InsightsListViewProps {
  projectId: string;
  userId?: string;
  onView?: (analysis: InsightsAnalysisDoc) => void;
  onCreate?: () => void;
  /** Register task for CanvasTasksPanel progress tracking */
  onTaskStarted?: (taskId: string, description: string) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────

const formatDate = (value?: unknown): React.ReactNode => {
  const date = toDate(value);
  if (!date) return '—';
  try {
    const dateLine = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    const timeLine = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    return (
      <Stack spacing={0}>
        <Typography variant="body2">{dateLine}</Typography>
        <Typography variant="body2" color="text.secondary">{timeLine}</Typography>
      </Stack>
    );
  } catch {
    return '—';
  }
};

const statusColor = (status?: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (status) {
    case 'completed': return 'success';
    case 'running': return 'info';
    case 'pending': return 'warning';
    case 'failed': return 'error';
    default: return 'default';
  }
};

const countManifestInsights = (analysis: InsightsAnalysisDoc): number => {
  const categoryPackages = analysis.categoryPackages;
  if (!categoryPackages || typeof categoryPackages !== 'object') return 0;
  return Object.values(categoryPackages as Record<string, any>).reduce((sum, pkg) => (
    sum + Number(pkg?.insightCount || 0)
  ), 0);
};

const countManifestQuestions = (analysis: InsightsAnalysisDoc): number => {
  const categoryPackages = analysis.categoryPackages;
  if (categoryPackages && typeof categoryPackages === 'object') {
    return Object.values(categoryPackages as Record<string, any>).reduce((sum, pkg) => (
      sum + (pkg?.hasQuestionSet ? 1 : 0)
    ), 0);
  }
  return 0;
};

const formatB2BScore = (analysis: InsightsAnalysisDoc): React.ReactNode => {
  const metrics = getB2BAudienceMetrics(analysis);
  if (metrics.length === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const score = averageAudienceScore(metrics);
  const primary = metrics.find((metric) => metric.score != null) ?? metrics[0];
  return (
    <Stack spacing={0.5} alignItems="center">
      {score != null ? (
        <Chip label={`${score}/100`} size="small" color="success" data-testid="insights-list-audience-score" />
      ) : (
        <Chip label="B2B" size="small" variant="outlined" />
      )}
      <Typography variant="caption" color="text.secondary" textAlign="center">
        {metrics.length} audience{metrics.length === 1 ? '' : 's'}
      </Typography>
      {primary?.stage && (
        <Typography variant="caption" color="text.secondary" textAlign="center">
          {primary.stage}
        </Typography>
      )}
    </Stack>
  );
};

const formatB2BReader = (analysis: InsightsAnalysisDoc): React.ReactNode => {
  const metrics = getB2BAudienceMetrics(analysis);
  if (metrics.length === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const primary = metrics.find((metric) => metric.readerDescription || metric.primaryReaderRole) ?? metrics[0];
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {primary.primaryReaderRole && <Chip label={primary.primaryReaderRole} size="small" />}
        <Chip label={formatReadiness(primary)} size="small" color="primary" variant="outlined" />
      </Stack>
      <Typography variant="body2" sx={{ maxWidth: 300, whiteSpace: 'normal' }}>
        {primary.readerDescription || primary.contentJob || primary.audienceName || primary.audienceId}
      </Typography>
    </Stack>
  );
};

/**
 * W12 Task A4 — the owning API refuses a delete with the FIRST blocking reference named
 * (`Insight analysis is referenced by <path> (<field>) and cannot be deleted.`).
 * Show that reason verbatim plus what the operator can do about that class of blocker:
 * a variable generation is deleted in the variables flow (never cascaded here), a baked
 * strategy version is immutable, and a running task blocks only until it finishes.
 */
const deleteFailureMessage = (error: unknown): string => {
  const reason = error instanceof Error && error.message.trim() ? error.message.trim() : '';
  if (!reason) return 'Failed to delete analyses';
  if (reason.includes('insightVariableGenerations')) return `${reason} Delete the variable generation first.`;
  if (reason.includes('/versions/')) return `${reason} A baked strategy version still uses this analysis; versions are immutable.`;
  if (reason.includes('backend_tasks')) return `${reason} A running task uses this analysis — wait for it to finish.`;
  return reason;
};

// ── Component ────────────────────────────────────────────────────────

export const InsightsListView: React.FC<InsightsListViewProps> = ({
  projectId,
  userId,
  onView,
  onCreate,
  onTaskStarted,
}) => {
  const [showCreateAIDialog, setShowCreateAIDialog] = useState(false);
  const [analyses, setAnalyses] = useState<InsightsAnalysisDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [rowSelection, setRowSelection] = useState<SDataTableRowSelectionState>({});

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [analysesToDelete, setAnalysesToDelete] = useState<InsightsAnalysisDoc[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  // Snackbar. Only the delete flow's snackbars are `persistent` (no auto-hide, click-away
  // ignored): a delete runs for minutes and «Deleting…» is its only progress signal.
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info'; persistent?: boolean }>({
    open: false, message: '', severity: 'success',
  });

  // Service
  const [service] = useState(() => new InsightsFirestoreService(projectId));

  // ── Real-time subscription ─────────────────────────────────────────

  useEffect(() => {
    const unsubscribe = service.onCollectionSnapshot(
      (data) => { setAnalyses(data); setLoading(false); },
      (error) => { console.error('Insights listener error:', error); setLoading(false); },
      { limit: null },
    );
    return () => unsubscribe();
  }, [service]);

  // ── Actions ────────────────────────────────────────────────────────

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection],
  );

  const selectedAnalyses = useMemo(
    () => analyses.filter((analysis) => selectedIds.includes(analysis.id)),
    [analyses, selectedIds],
  );

  const handleExport = useCallback((analysis: InsightsAnalysisDoc) => {
    InsightsExportService.download(analysis, `insights_${analysis.name || analysis.id}_${Date.now()}.json`);
    setSnackbar({ open: true, message: 'Exported successfully', severity: 'success' });
  }, []);

  const handleDeleteClick = useCallback((targets: InsightsAnalysisDoc[]) => {
    if (isDeleting) return;
    if (targets.length === 0) return;
    setAnalysesToDelete(targets);
    setDeleteDialogOpen(true);
  }, [isDeleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (analysesToDelete.length === 0) return;
    const targets = analysesToDelete;
    setIsDeleting(true);
    setDeleteDialogOpen(false);
    setAnalysesToDelete([]);
    setRowSelection({});
    setSnackbar({
      open: true,
      message: targets.length === 1 ? 'Deleting analysis...' : `Deleting ${targets.length} analyses...`,
      severity: 'info',
      persistent: true,
    });
    try {
      await service.deleteMany(targets.map((analysis) => analysis.id));
      const message = targets.length === 1
        ? `Deleted "${targets[0].name || 'Analysis'}"`
        : `Deleted ${targets.length} analyses`;
      setSnackbar({ open: true, message, severity: 'success' });
    } catch (error) {
      console.error('Delete failed:', error);
      setSnackbar({ open: true, message: deleteFailureMessage(error), severity: 'error', persistent: true });
    } finally {
      setIsDeleting(false);
    }
  }, [analysesToDelete, service]);

  // ── Table columns ──────────────────────────────────────────────────

  const columns = useMemo<SDataTableColumnDef<InsightsAnalysisDoc>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      size: 220,
      // MRT skeleton rows have EMPTY row.original — guard before rendering the chip.
      Cell: ({ row }) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{row.original.name || 'Untitled analysis'}</span>
          {row.original.id && (
            <TrustChip
              evidence={(row.original as Record<string, unknown>).evidence as never}
              testId={trustChipTestId(row.original.id)}
            />
          )}
        </Stack>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 110,
      Cell: ({ cell }) => {
        const value = cell.getValue<string>();
        return (
        <Chip label={value || 'unknown'} color={statusColor(value)} size="small" variant="outlined" />
        );
      },
    },
    {
      accessorKey: 'selectedActorRefs',
      header: 'Actors',
      size: 90,
      enableSorting: false,
      Cell: ({ cell }) => `${cell.getValue<any[]>()?.length ?? 0}`,
    },
    {
      id: 'b2bAudienceScore',
      header: 'Audience score',
      size: 130,
      enableSorting: false,
      Cell: ({ row }) => formatB2BScore(row.original),
    },
    {
      id: 'b2bReader',
      header: 'B2B reader',
      size: 280,
      enableSorting: false,
      Cell: ({ row }) => formatB2BReader(row.original),
    },
    {
      id: 'insightsCount',
      header: 'Insights',
      size: 90,
      enableSorting: false,
      Cell: ({ row }) => `${countManifestInsights(row.original)}`,
    },
    {
      id: 'questionsCount',
      header: 'Questions',
      size: 100,
      enableSorting: false,
      Cell: ({ row }) => `${countManifestQuestions(row.original)}`,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      size: 140,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
  ], []);

  // ── Empty state ────────────────────────────────────────────────────

  if (!loading && analyses.length === 0) {
    return (
      <Box>
        <Box display="flex" justifyContent="flex-end" mb={2} gap={1}>
          {userId && (
            <Button variant="contained" size="small" onClick={() => setShowCreateAIDialog(true)} startIcon={<AutoAwesomeIcon />} data-testid="insights-create-with-ai-btn">
              Create with AI
            </Button>
          )}
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={400} gap={2}>
          <PsychologyIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
          <Typography variant="h6" color="text.secondary">No insights analyses yet</Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={400}>
            Create an AI-powered analysis from selected actors.
          </Typography>
          <Stack direction="row" spacing={1}>
            {userId && (
              <Button variant="contained" onClick={() => setShowCreateAIDialog(true)} startIcon={<AutoAwesomeIcon />} data-testid="insights-create-with-ai-empty-btn">
                Create with AI
              </Button>
            )}
            {onCreate && (
              <Button variant="outlined" onClick={onCreate} startIcon={<AddIcon />}>
                Analyze Actors
              </Button>
            )}
          </Stack>
        </Box>

        <Snackbar open={snackbar.open} autoHideDuration={snackbar.persistent ? null : 3000} onClose={(_event, reason) => { if (reason === 'clickaway' && snackbar.persistent) return; setSnackbar(s => ({ ...s, open: false })); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
        </Snackbar>

        {/* Create with AI dialog (empty state) */}
        {userId && (
          <CreateInsightsDialog
            open={showCreateAIDialog}
            onClose={() => setShowCreateAIDialog(false)}
            projectId={projectId}
            userId={userId}
            onTaskStarted={onTaskStarted}
            onSuccess={() => {
              setSnackbar({ open: true, message: 'Insights analysis started! Track progress in the tasks panel.', severity: 'info' });
            }}
          />
        )}
      </Box>
    );
  }

  // ── Main render ────────────────────────────────────────────────────

  return (
    <Box sx={{ height: 'calc(100vh - 220px)', minHeight: 420, display: 'flex', flexDirection: 'column' }}>
      {/* Header actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" sx={{ mb: 0 }}>
            Insights analyses
          </Typography>
          {selectedIds.length > 0 && (
            <>
              <Chip
                label={selectedIds.length}
                size="small"
                color="primary"
                sx={{ fontWeight: 'fontWeightMedium' }}
                data-testid="insights-selected-count"
              />
              <Tooltip title="Delete selected">
                <IconButton
                  onClick={() => handleDeleteClick(selectedAnalyses)}
                  color="error"
                  size="small"
                  aria-label="Delete selected insights analyses"
                  data-testid="insights-bulk-delete"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                onClick={() => setRowSelection({})}
                sx={{ textTransform: 'none' }}
                data-testid="insights-clear-selection"
              >
                Clear selection
              </Button>
            </>
          )}
        </Box>
        <Box display="flex" justifyContent="flex-end" gap={1}>
          {userId && (
            <Button variant="contained" size="small" onClick={() => setShowCreateAIDialog(true)} startIcon={<AutoAwesomeIcon />}>
              Create with AI
            </Button>
          )}
          {onCreate && (
            <Button variant="outlined" size="small" onClick={onCreate} startIcon={<AddIcon />}>
              Analyze Actors
            </Button>
          )}
        </Box>
      </Box>

      {/* Table */}
      <SDataTable<InsightsAnalysisDoc>
        data={analyses}
        columns={columns}
        getRowId={(row) => row.id}
        enableRowSelection
        enableRowActions
        enableSorting
        enablePagination={false}
        enableStickyHeader
        enableTopToolbar={false}
        state={{ rowSelection, isLoading: loading }}
        onRowSelectionChange={setRowSelection}
        muiSelectCheckboxProps={{ size: 'small', color: 'primary' }}
        muiSelectAllCheckboxProps={{ size: 'small' }}
        muiTablePaperProps={{
          variant: 'outlined',
          elevation: 0,
          'data-testid': 'insights-list-table',
          sx: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
        } as any}
        muiTableContainerProps={{
          sx: { flex: 1, minHeight: 0, maxHeight: 'none', overflow: 'auto' },
        }}
        muiTableBodyRowProps={({ row }) => ({
          hover: true,
          onClick: () => {
            if (selectedIds.length > 0) {
              row.toggleSelected();
              return;
            }
            onView?.(row.original);
          },
          sx: { cursor: 'pointer' },
        })}
        renderRowActionMenuItems={({ row, closeMenu }) => [
          <MenuItem
            key="view"
            onClick={() => {
              closeMenu();
              onView?.(row.original);
            }}
            data-testid="insights-menu-view"
          >
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} /> View Details
          </MenuItem>,
          <MenuItem
            key="export"
            onClick={() => {
              closeMenu();
              handleExport(row.original);
            }}
            data-testid="insights-menu-export"
          >
            <FileDownloadIcon fontSize="small" sx={{ mr: 1 }} /> Export JSON
          </MenuItem>,
          <MenuItem
            key="delete"
            onClick={() => {
              closeMenu();
              handleDeleteClick([row.original]);
            }}
            sx={{ color: 'error.main' }}
            data-testid="insights-menu-delete"
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>,
        ]}
      />

      {/* Delete confirmation */}
      <SDialog
        open={deleteDialogOpen}
        onClose={() => {
          if (isDeleting) return;
          setDeleteDialogOpen(false);
          setAnalysesToDelete([]);
        }}
        maxWidth="sm"
        title={analysesToDelete.length > 1 ? 'Delete Analyses' : 'Delete Analysis'}
        primaryAction={{
          label: isDeleting ? 'Deleting...' : 'Delete',
          onClick: handleConfirmDelete,
          color: 'error',
          loading: isDeleting,
          disabled: isDeleting,
          'data-testid': 'insights-delete-confirm',
        }}
      >
        <Typography variant="body1">
          {analysesToDelete.length > 1 ? (
            `Are you sure you want to delete ${analysesToDelete.length} analyses?`
          ) : (
            <>
              Are you sure you want to delete &quot;
              <strong>{analysesToDelete[0]?.name || 'this analysis'}</strong>
              &quot;?
            </>
          )}
        </Typography>
        {analysesToDelete.length > 1 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {analysesToDelete.slice(0, 4).map((analysis) => analysis.name || analysis.id).join(', ')}
            {analysesToDelete.length > 4 ? `, and ${analysesToDelete.length - 4} more` : ''}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This will also delete frozen task snapshots and generated category/task packages. This action cannot be undone.
        </Typography>
      </SDialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={snackbar.persistent ? null : 3000} onClose={(_event, reason) => { if (reason === 'clickaway' && snackbar.persistent) return; setSnackbar(s => ({ ...s, open: false })); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>

      {/* Create with AI dialog */}
      {userId && (
        <CreateInsightsDialog
          open={showCreateAIDialog}
          onClose={() => setShowCreateAIDialog(false)}
          projectId={projectId}
          userId={userId}
          onTaskStarted={onTaskStarted}
          onSuccess={() => {
            setSnackbar({ open: true, message: 'Insights analysis started! Track progress in the tasks panel.', severity: 'info' });
          }}
        />
      )}
    </Box>
  );
};
